import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { invalidateBookmarkData } from '../queries/useBookmarks';
import { CATEGORY_META_QUERY_KEY } from '../queries/useCategories';

interface RenameCategoryInput {
  path: string;
  newName: string;
}

interface MoveCategoryInput {
  path: string;
  targetParent: string | null;
}

/** Server response for rename/move — echoes resolved paths (same-path = no-op). */
export interface CategoryChangeResponse {
  old_path: string;
  new_path: string;
  bookmarks_updated: number;
  category_meta_updated: number;
}

export function useRenameCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ path, newName }: RenameCategoryInput) =>
      apiFetch<CategoryChangeResponse>(`/api/categories/${encodeURIComponent(path)}/rename`, {
        method: 'POST',
        body: JSON.stringify({ new_name: newName }),
      }),
    onSettled: () => {
      invalidateBookmarkData(queryClient);
      queryClient.invalidateQueries({ queryKey: CATEGORY_META_QUERY_KEY });
    },
  });
}

export function useMoveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ path, targetParent }: MoveCategoryInput) =>
      apiFetch<CategoryChangeResponse>(`/api/categories/${encodeURIComponent(path)}/move`, {
        method: 'POST',
        body: JSON.stringify({ target_parent: targetParent }),
      }),
    onSettled: () => {
      invalidateBookmarkData(queryClient);
      queryClient.invalidateQueries({ queryKey: CATEGORY_META_QUERY_KEY });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ path, cascade }: { path: string; cascade?: boolean }) =>
      apiFetch<{ success: boolean; bookmarks_deleted: number; bookmarks_rehomed: number }>(`/api/categories/${encodeURIComponent(path)}${cascade ? '?cascade=true' : ''}`, {
        method: 'DELETE',
      }),
    onSettled: () => {
      invalidateBookmarkData(queryClient);
      queryClient.invalidateQueries({ queryKey: CATEGORY_META_QUERY_KEY });
    },
  });
}
